/**
 * RestrictedRoute Component
 * 
 * Wrapper for route components that require specific permissions
 * Automatically redirects to dashboard if user doesn't have access
 * Shows loading state while checking permissions
 * 
 * @example
 * ```tsx
 * // In App.tsx routes
 * case '/sharing':
 *   return (
 *     <RestrictedRoute page="sharing">
 *       <SharingPage userId={user.id} />
 *     </RestrictedRoute>
 *   );
 * 
 * case '/configurations':
 *   return (
 *     <RestrictedRoute page="configurations">
 *       <ConfigurationsPage />
 *     </RestrictedRoute>
 *   );
 * ```
 */

import { ReactNode, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { LoadingScreen } from '../primitives/LoadingScreen';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { ShieldAlert } from 'lucide-react';

type RestrictedPage = 'settings' | 'sharing' | 'configurations';

interface RestrictedRouteProps {
  children: ReactNode;
  page: RestrictedPage;
}

const PAGE_LABELS: Record<RestrictedPage, string> = {
  settings: 'Ajustes',
  sharing: 'Compartilhamento',
  configurations: 'Configurações',
};

/**
 * Route wrapper that checks permissions before rendering
 */
export function RestrictedRoute({ children, page }: RestrictedRouteProps) {
  const { canAccess, loading, role } = usePermissions();

  const hasAccess = canAccess(page);

  // Redirect to dashboard if no access
  useEffect(() => {
    if (!loading && !hasAccess) {
      // Small delay to show error message
      const timer = setTimeout(() => {
        window.location.hash = '#/';
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loading, hasAccess]);

  // Show loading while checking permissions
  if (loading) {
    return <LoadingScreen message="Verificando permissões..." />;
  }

  // Show access denied message
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Alert variant="destructive">
            <ShieldAlert className="h-5 w-5" />
            <AlertTitle className="text-lg font-semibold">Acesso Negado</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <p>
                Você não tem permissão para acessar <strong>{PAGE_LABELS[page]}</strong>.
              </p>
              {role && (
                <p className="text-sm">
                  Seu papel atual: <strong className="capitalize">{role}</strong>
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Apenas <strong>Proprietários</strong> e <strong>Administradores</strong> podem acessar esta página.
              </p>
              <p className="text-sm text-muted-foreground">
                Entre em contato com o administrador da empresa se você precisa de acesso.
              </p>
              <div className="pt-2">
                <Button 
                  onClick={() => window.location.hash = '#/'}
                  className="w-full"
                >
                  Voltar ao Dashboard
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // User has access, render children
  return <>{children}</>;
}

